import {
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Label,
  Row,
  Spinner,
  Toast,
  ToastBody,
  ToastHeader,
} from "reactstrap";
import { TripModal } from "../../../Components/Common/TripModal/tripModal";
import { useEffect, useRef, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllVehicleList, getAllDriverList } from "../../../slices/thunks";
import * as Yup from "yup";
import { Formik } from "formik";
import Select from "react-select";
import {
  assignTrips,
  getAllTripsList,
} from "../../../slices/tripsDetails/thunk";

export const AssignTripmodal = ({
  isModalOpen,
  toggleModal,
  tripsById,
  loading,
  pageSize,
  searchText,
  selectedItem,
}) => {
  const formikRef = useRef(null);
  const dispatch = useDispatch();
  const [showToast, setShowToast] = useState(false);
  const [toasterType, setToasterType] = useState("success");
  const [toastMessage, setToastMessage] = useState("");

  const driverList = useSelector(
    (state) => state.DriverManagementSlice.driverList
  );

  const vehicleList = useSelector((state) => state.Vehicle.vehicleList);

  const assignSchema = Yup.object().shape({
    drivers: Yup.array().min(1, "At least one driver is required").required(),
    vehicle: Yup.string().required("Vehicle is required"),
  });

  const updateInitialValues = {
    drivers: Array.isArray(tripsById?.drivers) ? tripsById.drivers : [],
    vehicle: tripsById?.vehicle || "",
  };

  const showToaster = (isSuccess, message) => {
    setShowToast(true);
    setToasterType(isSuccess ? "success" : "danger");
    setToastMessage(message);
    setTimeout(() => {
      setShowToast(false);
      toggleModal();
      dispatch(
        getAllTripsList(
          currentPage,
          pageSize,
          searchText,
          selectedItem.toLowerCase(),
          "",
          ""
        )
      );
      if (!isSuccess) {
        dispatch(hideErrorMessage());
      }
    }, 3000);
  };

  const handleAssignsubmit = async (values) => {
    try {
      const response = await dispatch(assignTrips(tripsById?.id, values));
      const { success: isSuccess, message } = response;
      showToaster(isSuccess, message);
    } catch (error) {
      showToaster(false, error.message || "An unexpected error occurred");
    }
  };

  useEffect(() => {
    try {
      dispatch(getAllDriverList("None", "None", ""));
      dispatch(getAllVehicleList("None", "None", ""));
    } catch (error) { }
  }, [dispatch]);

  const vehicleOptions = useMemo(() => {
    const allVehicles = Array.isArray(vehicleList) ? vehicleList : [];
    const activeVehicles = allVehicles.filter((v) => v.status === "active");

    let options = activeVehicles.map((vehicle) => ({
      value: String(vehicle.id),
      label: `${vehicle.number_plate}`,
      status: vehicle.status,
    }));

    if (updateInitialValues?.vehicle) {
      const selectedVehicle = allVehicles.find(
        (v) => String(v.id) === String(updateInitialValues.vehicle)
      );

      const alreadyExists = options.some(
        (opt) => opt.value === String(selectedVehicle?.id)
      );
      if (selectedVehicle && !alreadyExists) {
        options.push({
          value: String(selectedVehicle.id),
          label: `${selectedVehicle.number_plate}`,
          status: selectedVehicle.status,
        });
      }
    }

    return options;
  }, [vehicleList, updateInitialValues?.vehicle]);

  return (
    <TripModal
      isModalOpen={isModalOpen}
      toggleModal={toggleModal}
      buttonText="Assign"
      closeText={"Close without saving"}
      title={"Trip assignment"}
      tripText={`Trip ID: ${tripsById?.trip_id || ""}`}
      loading={loading}
      showcloseIcon={true}
      size="lg"
      onSubmit={() => {
        if (formikRef.current) {
          formikRef.current.handleSubmit();
        }
      }}
    >
      <div className="px-3 d-flex flex-column gap-4">
        <div
          className="position-fixed top-0 end-0 p-3"
          style={{ zIndex: 1055 }}
        >
          <Toast isOpen={showToast}>
            <ToastHeader
              icon={toasterType === "danger" ? "danger" : "success"}
              toggle={() => setShowToast(false)}
            >
              {toasterType === "danger" ? "Error" : "Success"}
            </ToastHeader>
            <ToastBody>{toastMessage}</ToastBody>
          </Toast>
        </div>
        <Formik
          innerRef={formikRef}
          enableReinitialize
          initialValues={updateInitialValues}
          validationSchema={assignSchema}
          onSubmit={handleAssignsubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="drivers" className="passwordLabel">
                        Driver(s)
                      </Label>
                      <Select
                        isMulti
                        name="drivers"
                        menuPortalTarget={document.body}
                        options={driverList
                          .filter((driver) => driver.status === "active")
                          .map((driver) => ({
                            value: driver.id,
                            label: `${driver.first_name} ${driver.last_name}`,
                          }))}
                        classNamePrefix="select"
                        value={
                          Array.isArray(values.drivers)
                            ? values.drivers.map((driverId) => {
                              const driver = driverList.find(
                                (d) => d.id === driverId
                              );
                              return {
                                value: driverId,
                                label: driver
                                  ? `${driver.first_name} ${driver.last_name}`
                                  : `Driver ID: ${driverId}`,
                              };
                            })
                            : []
                        }
                        onChange={(selectedOptions) => {
                          const selectedIds = selectedOptions
                            ? selectedOptions.map((option) => option.value)
                            : [];
                          setFieldValue("drivers", selectedIds);
                        }}
                        onBlur={() => {
                          handleBlur({ target: { name: "drivers" } });
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            backgroundColor: "#f4f4f4",
                            border: "none",
                            borderBottom: "1px solid #8d8d8d",
                            minHeight: "44px",
                            height: "43px",
                            boxShadow: "none",
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: "43px",
                            padding: "0 6px",
                            overflow: "auto",
                            backgroundColor: "transparent",
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: "43px",
                            backgroundColor: "transparent",
                          }),
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }), // 👈 Add this line
                        }}
                      />

                      {touched.drivers && errors.drivers ? (
                        <FormFeedback style={{ display: "block" }}>
                          {errors.drivers}
                        </FormFeedback>
                      ) : null}
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label for="vehicle" className="passwordLabel">
                        Vehicle
                      </Label>
                      <Select
                        name="vehicle"
                        classNamePrefix="select"
                        menuPortalTarget={document.body}
                        options={vehicleOptions}
                        value={
                          vehicleOptions.find(
                            (opt) =>
                              String(opt.value) === String(values.vehicle)
                          ) || null
                        }
                        onChange={(selectedOption) => {
                          setFieldValue("vehicle", selectedOption?.value || "");
                        }}
                        onBlur={() =>
                          handleBlur({ target: { name: "vehicle" } })
                        }
                        styles={{
                          control: (base) => ({
                            ...base,
                            backgroundColor: "#f4f4f4",
                            border: "none",
                            borderBottom: "1px solid #8d8d8d",
                            minHeight: "44px",
                            height: "43px",
                            boxShadow: "none",
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: "43px",
                            padding: "0 6px",
                            overflow: "auto",
                            backgroundColor: "transparent",
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: "43px",
                            backgroundColor: "transparent",
                          }),
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }), // 👈 Add this line
                        }}
                      />

                      {touched.vehicle && errors.vehicle ? (
                        <FormFeedback style={{ display: "block" }}>
                          {errors.vehicle}
                        </FormFeedback>
                      ) : null}
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </div>
    </TripModal>
  );
};
